import { createFileRoute, redirect } from '@tanstack/react-router';
import { parse, object, string, optional } from 'valibot';

import {
  getPatientCarePlansQueryKey,
  fetchPatientCarePlans,
} from '@/features/patients/hooks/usePatientCarePlans';

export const Route = createFileRoute('/(authenticated)/patients/$patientId')({
  validateSearch: (search) => {
    return parse(
      object({
        cpid: optional(string()),
        panel: optional(string()),
        taskId: optional(string()),
        labCode: optional(string()),
        labTab: optional(string()),
        labSort: optional(string()),
        labOrderFilter: optional(string()),
        /**
         * Care Team deep link — selects this member on arrival at the
         * /care-team route. Format matches CareTeamMember.id from
         * features/care-team/lib/careTeamData.ts (e.g., "provider:<pr-id>",
         * "alopex-coordinator:<pr-id>", "resource:<contained-id>",
         * "family:<index>").
         */
        memberId: optional(string()),
        /**
         * Communications deep link — opens the Log Communication modal on
         * /communications with this care team member pre-selected. Format is
         * the bot's `careTeamMemberRef` token: `PractitionerRole/{id}`,
         * `CareTeam/{id}#{contained}`, or `Patient/{id}#contact/{contactId}`.
         */
        memberRef: optional(string()),
        /**
         * Appointments deep link — opens the Add Appointment modal on
         * /appointments with this care team member pre-selected. Same token
         * format as `memberRef`, but family contacts are not supported by the
         * Appointments picker so the Care Team UI only emits this for
         * provider, resource, and Alopex Coordinator members.
         */
        apptMemberRef: optional(string()),
        /**
         * Appointments-modal mode for the deep link — `add` (default) for
         * provider/resource members, `cc-call` for Alopex Coordinator
         * members so the Care Team panel opens the right form.
         */
        apptMode: optional(string()),
        /**
         * Appointments view deep link — opens the View Appointment modal on
         * /appointments for the matching id. Used by CT-06 appointment
         * summary tiles in the Care Team detail.
         */
        apptId: optional(string()),
      }),
      search,
    );
  },
  beforeLoad: async ({ params, search, context }) => {
    if (search.cpid) return;

    const carePlans = await context.queryClient.fetchQuery({
      queryKey: getPatientCarePlansQueryKey(params.patientId),
      staleTime: 30_000,
      queryFn: () => fetchPatientCarePlans(params.patientId),
    });

    if (carePlans.programs.length === 1) {
      throw redirect({
        search: { ...search, cpid: carePlans.programs[0].cpid },
        replace: true,
      });
    }
  },
  loader: async ({ params, context }) => {
    await context.queryClient.prefetchQuery({
      queryKey: getPatientCarePlansQueryKey(params.patientId),
      queryFn: () => fetchPatientCarePlans(params.patientId),
    });
  },
});
